import type { DriftAccount, DriftQueueFilter } from '@interfaces/driftQueue';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import type { DataTableTanstackTable } from '@ovComponents/2-component/dataTable';
import { DataTable } from '@ovComponents/2-component/dataTable';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DriftQueueEmptyState } from './driftQueueEmptyState';
import { DriftQueueFooter } from './driftQueueFooter';
import { DriftQueueStatCards } from './driftQueueStatCards';
import { formatLastRefreshed } from './formatLastRefreshed';
import type { UseDriftQueueCounts } from './useDriftQueue';

/** Scroll cap for the table body — header, stat cards, and footer stay fixed. */
const TABLE_MAX_HEIGHT = '32vh';

export type DriftQueueVisualProps = {
  table: DataTableTanstackTable<DriftAccount>;
  counts: UseDriftQueueCounts;
  activeFilter: DriftQueueFilter;
  onFilterChange: (next: DriftQueueFilter) => void;
  isLoading: boolean;
  error: Error | null;
  totalRowCount: number;
  filteredRowCount: number;
  onRowClick: (account: DriftAccount) => void;
  /**
   * Override the page title (defaults to the `driftQueue:title` i18n key).
   * Pass a string to replace the heading entirely, e.g. for embedded use.
   */
  title?: string;
  /**
   * Timestamp of the last data refresh shown below the title.
   * Defaults to `new Date()` at render time when not provided.
   */
  lastRefreshedAt?: Date;
  /**
   * Handler for the "View All" header button.
   * When `undefined` the button is hidden — never renders a dead control.
   */
  onViewAllClick?: () => void;
  /** When false, hides the "As of …" line under the widget title (e.g. v2 dashboard). */
  showLastRefreshed?: boolean;
};

export const DriftQueueVisual: FC<DriftQueueVisualProps> = ({
  table,
  counts,
  activeFilter,
  onFilterChange,
  isLoading,
  error,
  totalRowCount,
  filteredRowCount,
  onRowClick,
  title,
  lastRefreshedAt,
  onViewAllClick,
  showLastRefreshed = true,
}) => {
  const { t } = useTranslation(['driftQueue']);
  const theme = useTheme();
  const { pageSize } = table.getState().pagination;

  const resolvedTitle = title ?? t('driftQueue:title');

  const refreshedLabel = t('driftQueue:lastRefresh.label', {
    value: formatLastRefreshed(lastRefreshedAt ?? new Date()),
  });

  const tableSection = (
    <DataTable<DriftAccount>
      table={table}
      rowsPerPage={pageSize}
      loading={isLoading}
      tableProps={{ size: 'small' }}
      emptyContent={
        <DriftQueueEmptyState
          filterHasNoMatches={filteredRowCount === 0 && totalRowCount > 0}
        />
      }
      tablePagination={
        filteredRowCount > 50
          ? {
              mode: 'tanstack',
              showPerPageSelect: true,
              pageSizes: [25, 50, 100],
            }
          : { mode: 'tanstack', showPerPageSelect: false, pageSizes: [25] }
      }
      getRowProps={(row) => ({
        hover: true,
        sx: { cursor: 'pointer' },
        onClick: () => onRowClick(row.original),
      })}
    />
  );

  return (
    <Box
      data-testid='drift-queue'
      sx={{
        p: 1.5,
        '& .MuiTableHead-root .MuiTableCell-root': {
          paddingTop: '8px',
          paddingBottom: '8px',
          whiteSpace: 'nowrap',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          paddingTop: '4px',
          paddingBottom: '4px',
        },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          pb: 1,
          borderBottom: `1px solid ${theme.color.outline}`,
        }}
      >
        {/* Left cluster: title (+ optional last-refresh on standalone pages) */}
        <Box>
          <Typography variant='headingSmall'>{resolvedTitle}</Typography>
          {showLastRefreshed && (
            <Typography variant='bodySmall' color='textSecondary'>
              {refreshedLabel}
            </Typography>
          )}
        </Box>

        {/* Right cluster: red alert indicator + View All */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {counts.needsAction > 0 && (
            <Box
              role='status'
              aria-label={String(
                t('driftQueue:header.queueAlertA11yLabel', {
                  count: counts.needsAction,
                }),
              )}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 10,
                backgroundColor: theme.color.negative,
                color: theme.color.onNegative,
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 16 }} />
              <Typography
                variant='labelSmall'
                sx={{ color: theme.color.onNegative, fontWeight: 700 }}
              >
                {t('driftQueue:header.queueAlertLabel', {
                  count: counts.needsAction,
                })}
              </Typography>
            </Box>
          )}

          {onViewAllClick && (
            <Button variant='outlined' size='small' onClick={onViewAllClick}>
              {t('driftQueue:header.viewAll')}
            </Button>
          )}
        </Box>
      </Box>
      {/* Error banner */}
      {error !== null && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 1,
            backgroundColor: theme.color.negativeVariant,
          }}
        >
          <Typography variant='bodySmall' component='p' color='error'>
            {error.message}
          </Typography>
        </Box>
      )}

      {/* Solid-color filter stat cards */}
      <DriftQueueStatCards
        activeFilter={activeFilter}
        onFilterChange={onFilterChange}
        counts={counts}
      />

      {/* Data table — only the data rows scroll. Vertical overflow lives on this
          wrapper (horizontal scroll is owned by {@link DataTableView}). Column
          headers stay pinned to the top and the pagination footer stays pinned
          to the bottom while rows scroll. */}
      <Box
        data-testid='drift-queue-table-scroll'
        sx={{
          maxHeight: TABLE_MAX_HEIGHT,
          overflowY: 'auto',
          '& .MuiTableHead-root .MuiTableCell-root': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: theme.color.surface,
          },
          // Pagination footer is a sibling of the horizontal-scroll wrapper inside
          // DataTableView — pin it to the bottom while rows scroll.
          '& [data-testid="data-table-horizontal-scroll"] + *': {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            backgroundColor: theme.color.surface,
          },
        }}
      >
        {tableSection}
      </Box>

      <DriftQueueFooter shown={filteredRowCount} total={totalRowCount} />
    </Box>
  );
};
